import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import Items from "@/pages/Items";
import NewInvoice from "@/pages/NewInvoice";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Clients from "@/pages/Clients";
import ClientRateCardPage from "@/pages/ClientRateCardPage";
import LeadsPage from "@/pages/LeadsPage";
import ImportPO from "@/pages/ImportPO";
import GstReports from "@/pages/GstReports";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import UsersPage from "@/pages/UsersPage";
import VendorsPage from "@/pages/VendorsPage";
import VendorDashboard from "@/pages/VendorDashboard";
import PurchasesPage from "@/pages/PurchasesPage";
import PurchaseReportsPage from "@/pages/PurchaseReportsPage";
import BankReconPage from "@/pages/BankReconPage";
import ClientOrdersPage from "@/pages/ClientOrdersPage";
import ExpensesPage from "@/pages/ExpensesPage";
import QRPosterPage from "@/pages/QRPosterPage";
import HomeLanding from "@/pages/HomeLanding";
import Reports from "@/pages/Reports";
import PrivateRoute from "@/components/PrivateRoute";
import { AuthProvider } from "@/context/AuthContext";

// --- Client Ordering Portal ---
import PortalLanding from "@/pages/portal/PortalLanding";
import PortalRegister from "@/pages/portal/PortalRegister";
import PortalSignIn from "@/pages/portal/PortalSignIn";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalOrder from "@/pages/portal/PortalOrder";
import PortalMyOrders from "@/pages/portal/PortalMyOrders";
import PlannerEntry from "@/pages/portal/planner/PlannerEntry";
import PlannerProfile from "@/pages/portal/planner/PlannerProfile";
import PlannerBuilder from "@/pages/portal/planner/PlannerBuilder";
import PlannerLists from "@/pages/portal/planner/PlannerLists";
import PlannerQuote from "@/pages/portal/planner/PlannerQuote";
import PlannerAdminPage from "@/pages/PlannerAdminPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import PurchaseOrderEditor from "@/pages/PurchaseOrderEditor";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
import { PortalAuthProvider } from "@/context/PortalAuthContext";

function AppShell() {
    return (
        <div className="app-shell">
            <Sidebar />
            <main className="app-main">
                <Routes>
                    {/* Public front page — hardinbachat.com/ acts as the
                        company website. Admin/staff can still open
                        /login → /dashboard directly.  Vanity paths
                        (/HDBBilling , /Clientlogin , /Register) go to the
                        appropriate app entry points. */}
                    <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                    <Route path="/HDBBilling"  element={<Navigate to="/login" replace />} />
                    <Route path="/hdbbilling"  element={<Navigate to="/login" replace />} />
                    <Route path="/HDBbilling"  element={<Navigate to="/login" replace />} />
                    <Route path="/operations"  element={<Navigate to="/login" replace />} />
                    <Route path="/Operations"  element={<Navigate to="/login" replace />} />
                    <Route path="/staff"       element={<Navigate to="/login" replace />} />
                    <Route path="/Staff"       element={<Navigate to="/login" replace />} />
                    <Route path="/team"        element={<Navigate to="/login" replace />} />
                    <Route path="/Team"        element={<Navigate to="/login" replace />} />
                    <Route path="/Register"    element={<Navigate to="/portal/register" replace />} />
                    <Route path="/register"    element={<Navigate to="/portal/register" replace />} />
                    <Route path="/signup"      element={<Navigate to="/portal/register" replace />} />
                    <Route path="/join"        element={<Navigate to="/portal/register" replace />} />
                    <Route path="/Clientlogin" element={<Navigate to="/portal" replace />} />
                    <Route path="/clientlogin" element={<Navigate to="/portal" replace />} />
                    <Route path="/ClientLogin" element={<Navigate to="/portal" replace />} />
                    <Route path="/customer"    element={<Navigate to="/portal" replace />} />
                    <Route path="/Customer"    element={<Navigate to="/portal" replace />} />
                    <Route path="/customers"   element={<Navigate to="/portal" replace />} />
                    <Route path="/shop"        element={<Navigate to="/portal" replace />} />
                    <Route path="/order"       element={<Navigate to="/portal" replace />} />
                    <Route path="/items" element={<PrivateRoute minRole="admin"><Items /></PrivateRoute>} />
                    <Route path="/invoices" element={<PrivateRoute><Invoices /></PrivateRoute>} />
                    <Route path="/invoices/new" element={<PrivateRoute minRole="invoicing_staff"><NewInvoice /></PrivateRoute>} />
                    <Route path="/invoices/from-po" element={<PrivateRoute minRole="invoicing_staff"><ImportPO /></PrivateRoute>} />
                    <Route path="/invoices/:id/edit" element={<PrivateRoute minRole="invoicing_staff"><NewInvoice /></PrivateRoute>} />
                    <Route path="/invoices/:id" element={<PrivateRoute><InvoiceDetail /></PrivateRoute>} />
                    <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
                    <Route path="/clients/rate-card" element={<PrivateRoute><ClientRateCardPage /></PrivateRoute>} />
                    <Route path="/leads" element={<PrivateRoute><LeadsPage /></PrivateRoute>} />
                    <Route path="/vendors" element={<PrivateRoute><VendorsPage /></PrivateRoute>} />
                    <Route path="/vendors/:id" element={<PrivateRoute><VendorDashboard /></PrivateRoute>} />
                    <Route path="/purchases" element={<PrivateRoute><PurchasesPage /></PrivateRoute>} />
                    <Route path="/purchase-reports" element={<PrivateRoute><PurchaseReportsPage /></PrivateRoute>} />
                    <Route path="/bank-recon" element={<PrivateRoute><BankReconPage /></PrivateRoute>} />
                    <Route path="/client-orders" element={<PrivateRoute><ClientOrdersPage /></PrivateRoute>} />
                    <Route path="/purchase-orders" element={<PrivateRoute><PurchaseOrdersPage /></PrivateRoute>} />
                    <Route path="/purchase-orders/new" element={<PrivateRoute minRole="invoicing_staff"><PurchaseOrderEditor /></PrivateRoute>} />
                    <Route path="/purchase-orders/:id/edit" element={<PrivateRoute minRole="invoicing_staff"><PurchaseOrderEditor /></PrivateRoute>} />
                    <Route path="/purchase-orders/:id" element={<PrivateRoute><PurchaseOrderDetail /></PrivateRoute>} />
                    <Route path="/planner-admin" element={<PrivateRoute minRole="invoicing_staff"><PlannerAdminPage /></PrivateRoute>} />
                    <Route path="/plan" element={<Navigate to="/portal/essentials-planner" replace />} />
                    <Route path="/expenses" element={<PrivateRoute><ExpensesPage /></PrivateRoute>} />
                    <Route path="/qr-poster" element={<PrivateRoute minRole="admin"><QRPosterPage /></PrivateRoute>} />
                    <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
                    <Route path="/reports/gstr1" element={<PrivateRoute><GstReports /></PrivateRoute>} />
                    <Route path="/users" element={<PrivateRoute minRole="admin"><UsersPage /></PrivateRoute>} />
                    <Route path="/settings" element={<PrivateRoute minRole="admin"><Settings /></PrivateRoute>} />
                </Routes>
            </main>
        </div>
    );
}

function AppRouter() {
    const location = useLocation();
    // Handle Emergent OAuth callback (session_id in URL fragment) synchronously
    if (location.hash?.includes("session_id=")) {
        return <AuthProvider><AuthCallback /></AuthProvider>;
    }
    // Portal routes get their own auth provider and no admin sidebar
    if (location.pathname === "/portal" || location.pathname.startsWith("/portal/")) {
        return (
            <PortalAuthProvider>
                <Routes>
                    <Route path="/portal" element={<PortalLanding />} />
                    <Route path="/portal/register" element={<PortalRegister />} />
                    <Route path="/portal/signin" element={<PortalSignIn />} />
                    <Route path="/portal/essentials-planner" element={<PlannerEntry />} />
                    <Route path="/portal/essentials-planner/lists" element={<PlannerLists />} />
                    <Route path="/portal/essentials-planner/list/:id" element={<PlannerBuilder />} />
                    <Route path="/portal/essentials-planner/quotes/:id" element={<PlannerQuote />} />
                    <Route path="/portal/essentials-planner/:type/start" element={<PlannerProfile />} />
                    <Route path="/portal/*" element={<PortalLayout />}>
                        <Route path="dashboard" element={<PortalDashboard />} />
                        <Route path="order" element={<PortalOrder />} />
                        <Route path="orders" element={<PortalMyOrders />} />
                    </Route>
                </Routes>
            </PortalAuthProvider>
        );
    }
    // Admin app (default)
    return (
        <AuthProvider>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<HomeLanding />} />
                <Route path="/*" element={<AppShell />} />
            </Routes>
        </AuthProvider>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AppRouter />
                <Toaster position="bottom-right" richColors duration={2500} />
            </BrowserRouter>
        </div>
    );
}

export default App;
